import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Gift, Loader2, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail, toTakkState } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";

const HERO =
  "https://images.unsplash.com/photo-1709548145082-04d0cde481d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxjb3p5JTIwZWxlZ2FudCUyMGRhcmslMjByZXN0YXVyYW50JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzkwNzkzMzc0fDA&ixlib=rb-4.1.0&q=85";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[2-9]\d{7}$/;
export const normalizePhone = (v) => {
  let d = v.replace(/\D/g, "");
  if (d.startsWith("0047")) d = d.slice(4);
  else if (d.startsWith("47") && d.length === 10) d = d.slice(2);
  return d;
};

export default function Registrering() {
  const navigate = useNavigate();
  const [fornavn, setFornavn] = useState("");
  const [epost, setEpost] = useState("");
  const [telefon, setTelefon] = useState("");
  const [fodselsdato, setFodselsdato] = useState("");
  const [godtarVilkar, setGodtarVilkar] = useState(false);
  const [samtykkeSms, setSamtykkeSms] = useState(false);
  const [samtykkeEpost, setSamtykkeEpost] = useState(false);
  const markedsforing = samtykkeSms && samtykkeEpost;
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);

  const cleanPhone = normalizePhone(telefon);
  const validFornavn = fornavn.trim().length >= 2;
  const validEpost = EMAIL_RE.test(epost.trim());
  const validTelefon = PHONE_RE.test(cleanPhone);
  const todayStr = new Date().toISOString().split("T")[0];
  const validFodselsdato = (() => {
    if (!fodselsdato) return false;
    const d = new Date(fodselsdato);
    if (isNaN(d.getTime())) return false;
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return d <= today && d.getFullYear() >= 1900;
  })();

  const canSubmit = useMemo(
    () =>
      validFornavn &&
      validEpost &&
      validTelefon &&
      validFodselsdato &&
      godtarVilkar &&
      markedsforing &&
      !submitting,
    [validFornavn, validEpost, validTelefon, validFodselsdato, godtarVilkar, markedsforing, submitting]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        fornavn: fornavn.trim(),
        epost: epost.trim(),
        telefon: cleanPhone,
        fodselsdato: fodselsdato,
        godtar_vilkar: godtarVilkar,
        markedsforing_samtykke: markedsforing,
        samtykke_sms: samtykkeSms,
        samtykke_epost: samtykkeEpost,
      };
      const res = await api.post("/verify/start", payload);
      if (res.data.already_registered) {
        navigate("/takk", { state: toTakkState(res.data.registration) });
        return;
      }
      navigate("/bekreft", {
        state: { verificationId: res.data.verification_id, epost: payload.epost, telefon: cleanPhone, kanaler: res.data.kanaler },
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Noe gikk galt");
      setSubmitting(false);
    }
  };

  const fieldError = (show, msg) =>
    touched && show ? (
      <p className="mt-1.5 text-xs text-red-400" data-testid="field-error">
        {msg}
      </p>
    ) : null;

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#1A1715]">
      {/* Background */}
      <div className="absolute inset-0">
        <img src={HERO} alt="" className="animate-bg-drift h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1715]/45 via-[#1A1715]/60 to-[#1A1715]/95" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-xl flex-col px-5 py-8 sm:py-12">
        <header className="flex items-center justify-center animate-fade-in">
          <Logo />
        </header>

        <main className="mt-8 flex-1">
          <div className="animate-fade-up text-center" style={{ animationDelay: "0.05s" }}>
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10">
              <Gift className="h-6 w-6 text-[#D4AF37]" />
            </div>
            <h1 className="font-serif-display text-4xl font-600 leading-tight text-[#FDFBF7] sm:text-5xl">
              Få en gave fra Demo 🎁
            </h1>
            <p className="mx-auto mt-4 max-w-md text-base text-[#FDFBF7]/90">
              Registrer deg i dag, så får du en gave fra oss!
            </p>
            <p className="mx-auto mt-2 max-w-md text-sm text-[#A8A29E]">
              Kupongen blir tilgjengelig med en gang, og du viser den enkelt til servitøren fra telefonen din.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="mt-8 animate-fade-up rounded-3xl border border-white/10 bg-[#282421]/90 p-6 shadow-2xl backdrop-blur-md sm:p-8"
            style={{ animationDelay: "0.15s" }}
            data-testid="registration-form"
            noValidate
          >
            <div className="flex flex-col gap-5">
              {/* Fornavn */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#FDFBF7]" htmlFor="fornavn">
                  Fornavn
                </label>
                <input
                  id="fornavn"
                  type="text"
                  value={fornavn}
                  onChange={(e) => setFornavn(e.target.value)}
                  placeholder="Ola"
                  data-testid="input-fornavn"
                  className="h-14 w-full rounded-xl border border-white/10 bg-[#1A1715] px-4 text-[#FDFBF7] placeholder:text-[#A8A29E]/60 outline-none transition-colors focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40"
                />
                {fieldError(!validFornavn, "Skriv inn fornavnet ditt.")}
              </div>

              {/* E-post */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#FDFBF7]" htmlFor="epost">
                  E-post
                </label>
                <input
                  id="epost"
                  type="email"
                  value={epost}
                  onChange={(e) => setEpost(e.target.value)}
                  placeholder="ola@epost.no"
                  data-testid="input-epost"
                  className="h-14 w-full rounded-xl border border-white/10 bg-[#1A1715] px-4 text-[#FDFBF7] placeholder:text-[#A8A29E]/60 outline-none transition-colors focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40"
                />
                {fieldError(!validEpost, "Skriv inn en gyldig e-postadresse.")}
              </div>

              {/* Telefon */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#FDFBF7]" htmlFor="telefon">
                  Telefonnummer
                </label>
                <input
                  id="telefon"
                  type="tel"
                  inputMode="tel"
                  value={telefon}
                  onChange={(e) => setTelefon(e.target.value)}
                  placeholder="+47 400 00 000"
                  data-testid="input-telefon"
                  className="h-14 w-full rounded-xl border border-white/10 bg-[#1A1715] px-4 text-[#FDFBF7] placeholder:text-[#A8A29E]/60 outline-none transition-colors focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40"
                />
                {fieldError(!validTelefon, "Skriv inn et gyldig norsk telefonnummer (8 sifre).")}
              </div>

              {/* Fødselsdato */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#FDFBF7]" htmlFor="fodselsdato">
                  Fødselsdato
                </label>
                <input
                  id="fodselsdato"
                  type="date"
                  value={fodselsdato}
                  max={todayStr}
                  min="1900-01-01"
                  onChange={(e) => setFodselsdato(e.target.value)}
                  data-testid="input-fodselsdato"
                  className="date-input block h-14 w-full min-w-0 appearance-none rounded-xl border border-white/10 bg-[#1A1715] px-4 text-left text-[#FDFBF7] placeholder:text-[#A8A29E]/60 outline-none transition-colors focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40 [color-scheme:dark]"
                />
                {fieldError(!validFodselsdato, "Velg en gyldig fødselsdato.")}
              </div>

              {/* Samtykke – vilkår (obligatorisk) */}
              <label
                className="mt-1 flex cursor-pointer items-start gap-3 rounded-xl border border-white/10 bg-[#1A1715]/60 p-4"
                data-testid="consent-terms-wrapper"
              >
                <input
                  type="checkbox"
                  checked={godtarVilkar}
                  onChange={(e) => setGodtarVilkar(e.target.checked)}
                  data-testid="checkbox-terms"
                  className="mt-0.5 h-5 w-5 flex-shrink-0 accent-[#D4AF37]"
                />
                <span className="text-sm text-[#FDFBF7]">
                  Jeg godtar Demos{" "}
                  <Link
                    to="/vilkar"
                    target="_blank"
                    className="font-semibold text-[#D4AF37] underline underline-offset-2 hover:text-[#E5C564]"
                    data-testid="link-vilkar"
                    onClick={(e) => e.stopPropagation()}
                  >
                    vilkår
                  </Link>{" "}
                  og{" "}
                  <Link
                    to="/personvern"
                    target="_blank"
                    className="font-semibold text-[#D4AF37] underline underline-offset-2 hover:text-[#E5C564]"
                    data-testid="link-personvern"
                    onClick={(e) => e.stopPropagation()}
                  >
                    personvernerklæring
                  </Link>
                  .
                </span>
              </label>
              {fieldError(!godtarVilkar, "Du må godta vilkårene for å fortsette.")}

              {/* Samtykke – markedsføring (SMS og e-post, begge påkrevd) */}
              <div className="space-y-2">
                <label
                  className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/10 bg-[#1A1715]/60 p-4"
                  data-testid="consent-sms-wrapper"
                >
                  <input
                    type="checkbox"
                    checked={samtykkeSms}
                    onChange={(e) => setSamtykkeSms(e.target.checked)}
                    data-testid="checkbox-marketing-sms"
                    className="mt-0.5 h-5 w-5 flex-shrink-0 accent-[#D4AF37]"
                  />
                  <span className="text-sm text-[#FDFBF7]">
                    Ja, jeg ønsker å motta <strong>SMS</strong> med tilbud, nyheter og invitasjoner fra Demo.
                  </span>
                </label>
                <label
                  className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/10 bg-[#1A1715]/60 p-4"
                  data-testid="consent-epost-wrapper"
                >
                  <input
                    type="checkbox"
                    checked={samtykkeEpost}
                    onChange={(e) => setSamtykkeEpost(e.target.checked)}
                    data-testid="checkbox-marketing-epost"
                    className="mt-0.5 h-5 w-5 flex-shrink-0 accent-[#D4AF37]"
                  />
                  <span className="text-sm text-[#FDFBF7]">
                    Ja, jeg ønsker å motta <strong>e-post</strong> med tilbud, nyheter og invitasjoner fra Demo.
                  </span>
                </label>
                {fieldError(!markedsforing, "Du må krysse av for både SMS og e-post for å fortsette.")}
                <p className="mt-1.5 pl-1 text-xs text-[#A8A29E]/80">
                  Du kan når som helst trekke tilbake samtykket ditt.
                </p>
              </div>

              {/* Knapp */}
              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="submit-registration-btn"
                className="mt-2 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-base font-700 uppercase tracking-wide text-[#1A1715] transition-transform transition-colors hover:bg-[#E5C564] hover:scale-[1.02] active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-[#D4AF37]/25 disabled:text-[#1A1715]/50 disabled:hover:scale-100"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" /> Sender…
                  </>
                ) : (
                  <>Send meg bekreftelseskode</>
                )}
              </button>

              <div className="flex items-center justify-center gap-1.5 text-xs text-[#A8A29E]">
                <ShieldCheck className="h-3.5 w-3.5" />
                Trygg registrering. Én kupong per gjest.
              </div>
            </div>
          </form>
        </main>

        <footer className="mt-8 flex flex-col items-center gap-3 text-center text-xs text-[#A8A29E]/60">
          <Link
            to="/logg-inn"
            className="rounded-full border border-[#D4AF37]/30 px-5 py-2.5 text-sm text-[#E5C564] transition-colors hover:border-[#D4AF37] hover:bg-[#D4AF37]/10"
            data-testid="link-min-kupong"
          >
            Allerede registrert? Logg inn
          </Link>
          <Link to="/admin" className="hover:text-[#D4AF37]" data-testid="link-admin">
            Admin
          </Link>
        </footer>
      </div>
    </div>
  );
}
